"use client";

import React, { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

/**
 * global-error.tsx catches errors thrown by the root layout itself. It replaces
 * the entire root layout, so none of the app providers (LoggerProvider, tenant
 * theme, jux) are available here — we must talk to Sentry directly and render a
 * self-contained, unstyled-dependency-free fallback.
 *
 * Since the migration to @sentry/nextjs (APP-24209) the client SDK is
 * initialised in `instrumentation-client.ts` before the React tree hydrates, so
 * this capture is no longer gated on LoggerProvider's effect having run.
 */
export default function GlobalError({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	useEffect(() => {
		Sentry.captureException(error);
	}, [error]);

	return (
		<html lang="en">
			<body
				style={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					padding: "24px",
					backgroundColor: "#ffffff",
					fontFamily:
						"-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
					color: "#1a1a1a",
				}}
			>
				<div style={{ maxWidth: "360px", textAlign: "center" }}>
					<h1
						style={{
							fontSize: "24px",
							fontWeight: 600,
							marginBottom: "16px",
						}}
					>
						An unexpected error has occurred
					</h1>
					<p
						style={{
							fontSize: "16px",
							lineHeight: 1.5,
							marginBottom: "24px",
							color: "#4a4a4a",
						}}
					>
						Please refresh the page and try again. If the problem persists,
						please contact us for further assistance.
					</p>
					<button
						type="button"
						onClick={reset}
						style={{
							cursor: "pointer",
							padding: "10px 20px",
							fontSize: "14px",
							fontWeight: 500,
							borderRadius: "6px",
							border: "1px solid #1a1a1a",
							backgroundColor: "transparent",
							color: "#1a1a1a",
						}}
					>
						Refresh this page
					</button>
				</div>
			</body>
		</html>
	);
}
